import { memo, useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import { Autocomplete } from '@repo/videa-components/src/index';
import { useAtom } from 'jotai';

import { searchAtom } from '../../../modules/patientList/atoms';
import {
  type PatientListPageLayoutContext,
  PatientListPageLayoutContextMap,
} from '../../../modules/patientList/types';
import { referralsSearchAtom } from '../../../modules/referrals/atoms';
import { usePatientNameSearch } from '../../../react-query/usePatientNameSearch';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';

type PatientSearchProps = {
  context: PatientListPageLayoutContext;
};

export const PatientSearch = memo(({ context }: PatientSearchProps) => {
  // Use different search atoms based on context
  const searchAtomToUse =
    context === PatientListPageLayoutContextMap.Referrals
      ? referralsSearchAtom
      : searchAtom;

  const [search, setSearch] = useAtom(searchAtomToUse);
  const [patientName, setPatientName] = useState<string | null>(null);
  const { data: patientNames } = usePatientNameSearch(search);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();

  return (
    <Autocomplete
      options={
        patientNames?.map((name) => ({ label: name, value: name })) || []
      }
      value={patientName || ''}
      onChange={(newValue) => {
        setPatientName(newValue as string | null);
        setSearch((newValue as string) || '');
        trackEvent(mixpanelEvents.huddleNameSearchDropdownClick, {
          context,
        });
      }}
      search={search}
      onInputChange={(newValue) => {
        setSearch(newValue);
        trackEvent(mixpanelEvents.huddleNameSearchInput, {
          context,
        });
      }}
      debounceMs={300}
      dataTestId="search-patient-field"
      placeholder={t`Search`}
      noOptionsText={
        context === PatientListPageLayoutContextMap.Claims
          ? t`Search patients`
          : t`Search by patient name.`
      }
    />
  );
});

PatientSearch.displayName = 'PatientSearch';
